import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { adminApi } from '../api/admin';
import type { Service } from '../api/types';
import { StatusDot } from './StatusDot';

function tone(s: Service): 'ok' | 'warn' | 'crit' | 'muted' {
  if (s.last_state && s.last_state !== 'Running' && s.last_state !== 'Unknown') return 'crit';
  if (s.last_health === 'fail') return 'crit';
  if (s.last_state === 'Unknown' && s.last_health === null) return 'muted';
  if (s.last_state === 'Unknown') return 'warn';
  if (s.last_state === 'Running' && s.last_health === 'ok') return 'ok';
  return 'warn';
}

function relTime(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (Number.isNaN(secs)) return '—';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

export function ServiceTile({ service }: { service: Service }) {
  const t = tone(service);
  const { mutate } = useSWRConfig();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const borderClass =
    t === 'crit'
      ? 'border-cockpit-crit'
      : t === 'warn'
        ? 'border-cockpit-warn'
        : 'border-cockpit-border';

  async function restart() {
    if (!service.nssm_service) return;
    if (!confirm(`Restart NSSM service '${service.nssm_service}'?`)) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await adminApi.restartService(service.id);
      if (res.ok) {
        setFeedback({ ok: true, msg: 'restart triggered' });
        setTimeout(
          () => mutate((key) => typeof key === 'string' && key.startsWith('/api/services')),
          4000,
        );
        setTimeout(() => setFeedback(null), 6000);
      } else {
        setFeedback({ ok: false, msg: res.error ?? 'failed' });
      }
    } catch (e) {
      setFeedback({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`rounded-lg border ${borderClass} bg-cockpit-panel p-3`}>
      <header className="mb-2 flex items-center gap-2">
        <StatusDot tone={t} pulse={t === 'crit'} />
        <h4 className="text-sm font-semibold">{service.name}</h4>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-cockpit-dim">
          {service.env} · {service.kind}
        </span>
      </header>
      <dl className="grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-cockpit-dim">State</dt>
        <dd
          className={
            service.last_state === 'Running'
              ? 'text-cockpit-ok'
              : service.last_state === 'Unknown'
                ? 'text-cockpit-dim'
                : 'text-cockpit-crit'
          }
        >
          {service.last_state ?? '—'}
        </dd>
        <dt className="text-cockpit-dim">Health</dt>
        <dd
          className={
            service.last_health === 'ok'
              ? 'text-cockpit-ok'
              : service.last_health === 'fail'
                ? 'text-cockpit-crit'
                : 'text-cockpit-dim'
          }
        >
          {service.last_status_code !== null
            ? `${service.last_status_code} · ${service.last_response_ms ?? '—'} ms`
            : service.last_health === 'fail'
              ? 'unreachable'
              : '—'}
        </dd>
        <dt className="text-cockpit-dim">Updated</dt>
        <dd className="text-cockpit-text">
          {relTime(service.last_health_ts ?? service.last_state_ts)}
        </dd>
      </dl>
      {service.nssm_service && (
        <div className="mt-2 flex items-center justify-between border-t border-cockpit-border pt-2 text-[11px]">
          <button
            type="button"
            disabled={busy}
            onClick={() => void restart()}
            className="text-cockpit-dim hover:text-cockpit-text disabled:opacity-40"
          >
            {busy ? 'restarting…' : '↻ Restart'}
          </button>
          {feedback && (
            <span className={feedback.ok ? 'text-cockpit-ok' : 'text-cockpit-crit'}>
              {feedback.msg}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
